import React, { useEffect, useState, useCallback, useRef } from "react";
import { Badge, Button, Input, InputGroup, Table, Dropdown } from "rsuite";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faSearch, faFilter, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { EPlatform, IApp, IntegrationVersion, AppStatus } from "@bprotsyk/aso-core";
import { AppListItem } from "./app-list-item";
import SearchIcon from "@rsuite/icons/Search";
import ReloadIcon from "@rsuite/icons/Reload";

const { Column, HeaderCell, Cell } = Table;

// Enum для типів директу
export enum EDirectType {
  KEITARO_OFFER = 'keitaroOffer',
  OFFER_DIRECT = 'offerDirect'
}

interface ISearchInput {
  search: string;
  setSearch: (value: string) => void;
}

const CustomInputGroup = ({ search, setSearch }: ISearchInput) => (
  <div className="lists-search" style={{ display: 'flex', alignItems: 'center' }}>
    <div style={{ position: 'relative', flex: 1 }}>
      <input
        type="text"
        placeholder="Пошук..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="lists-search-input"
      />
      <FontAwesomeIcon icon={faSearch} className="lists-search-icon" />
    </div>
  </div>
);

interface AppsTableProps {
  apps: IApp[];
  loading: boolean;
  onAppClick: (app: IApp) => void;
  onPlatformClick: (app: IApp, platform: string) => void;
  onStatusChange: (appId: number, newStatus: AppStatus) => void;
  openDropdownId: number | null;
  dropdownPosition: { x: number; y: number } | null;
  onStatusCellClick: (appId: number, event: React.MouseEvent) => void;
  dropdownRefs: React.MutableRefObject<Map<number, HTMLDivElement>>;
  sortColumn: string;
  sortType: 'asc' | 'desc';
  onSort: (dataKey: string, sortType: 'asc' | 'desc' | undefined) => void;
  tableHeight: number;
  search: string;
  onSearchChange: (value: string) => void;
  onCreateApp: () => void;
  onRefresh: () => void;
  filters: {
    hideArchived: boolean;
    hideInDevelopment: boolean;
  };
  onFilterChange: (filterKey: string) => void;
}

// Helper functions
// getIntegrationVersionText removed - integrationVersion field no longer exists

const isValidAndroidPackage = (bundle: string): boolean => {
  if (!bundle) return false;
  const androidPackageRegex = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;
  return androidPackageRegex.test(bundle);
};

const getGooglePlayUrl = (bundle: string): string => {
  return `https://play.google.com/store/apps/details?id=${encodeURIComponent(bundle)}`;
};

const getStatusColor = (status: AppStatus, isChanged: boolean = false): string => {
  if (isChanged) {
    return "#ffc107";
  }
  
  switch (status) {
    case AppStatus.ARCHIVED:
      return "#6c757d";
    case AppStatus.IN_DEVELOPMENT:
      return "#007bff";
    case AppStatus.READY:
      return "#28a745";
    default:
      return "#6c757d";
  }
};

const getStatusText = (status: AppStatus): string => {
  switch (status) {
    case AppStatus.ARCHIVED:
      return "Архів";
    case AppStatus.IN_DEVELOPMENT:
      return "В розробці";
    case AppStatus.READY:
      return "Готовий";
    default:
      return "Невідомо";
  }
};

const getAvailableStatusTransitions = (currentStatus: AppStatus): AppStatus[] => {
  switch (currentStatus) {
    case AppStatus.ARCHIVED:
      return [AppStatus.IN_DEVELOPMENT, AppStatus.READY];
    case AppStatus.IN_DEVELOPMENT:
      return [AppStatus.ARCHIVED, AppStatus.READY];
    case AppStatus.READY:
      return [AppStatus.ARCHIVED, AppStatus.IN_DEVELOPMENT];
    default:
      return [AppStatus.ARCHIVED, AppStatus.IN_DEVELOPMENT, AppStatus.READY];
  }
};

const getStatusWeight = (status: AppStatus): number => {
  switch (status) {
    case AppStatus.READY:
      return 1;
    case AppStatus.IN_DEVELOPMENT:
      return 2;
    case AppStatus.ARCHIVED:
      return 3;
    default:
      return 4;
  }
};

const getSortValue = (app: IApp, key: string): string | number => {
  switch (key) {
    case 'id':
      return app.id;
    case 'name':
      return app.name || '';
    case 'bundle':
      return app.bundle || '';
    case 'status':
      return app.status;
    default:
      return '';
  }
};

const AppsTable: React.FC<AppsTableProps> = ({
  apps,
  loading,
  onAppClick,
  onPlatformClick,
  onStatusChange,
  openDropdownId,
  dropdownPosition,
  onStatusCellClick,
  dropdownRefs,
  sortColumn,
  sortType,
  onSort,
  tableHeight,
  search,
  onSearchChange,
  onCreateApp,
  onRefresh,
  filters,
  onFilterChange
}) => {
  const platformsKeys = [
    ...new Set(apps?.flatMap((app) => Object?.keys(app?.platforms || {}))),
  ];

  return (
    <div className="apps-table">
      <div className="apps-header">
        <div className="apps-header-content">
          <div className="apps-actions">
            <button
              className="lists-tab create-app-btn"
              onClick={onCreateApp}
            >
              Створити новий додаток
            </button>
            <span className="apps-separator" />
            <button
              className="lists-tab refresh-btn"
              onClick={onRefresh}
            >
              Оновити
              <ReloadIcon className="refresh-icon" />
            </button>
            <span className="apps-separator" />
            <label 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                color: '#ecf0f1',
                fontSize: '14px',
                userSelect: 'none'
              }}
              onClick={(e) => {
                e.preventDefault();
                onFilterChange('hideArchived');
              }}
            >
              <input
                type="checkbox"
                checked={filters.hideArchived}
                onChange={(e) => {
                  e.stopPropagation();
                  onFilterChange('hideArchived');
                }}
                style={{ 
                  margin: 0,
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px'
                }}
              />
              <span>Приховати архівовані</span>
            </label>
          </div>
          <div className="apps-search">
            <CustomInputGroup 
              search={search} 
              setSearch={onSearchChange} 
            />
          </div>
        </div>
      </div>

      <Table
        data={apps.filter(app => {
          // Фільтрація за статусом
          if (filters.hideArchived && app.status === AppStatus.ARCHIVED) return false;
          if (filters.hideInDevelopment && app.status === AppStatus.IN_DEVELOPMENT) return false;

          return true;
        }).sort((a, b) => {
          // Спочатку сортуємо за статусом
          const statusCompare = getStatusWeight(a.status) - getStatusWeight(b.status);
          if (statusCompare !== 0) return statusCompare;
          
          // Якщо статуси однакові, використовуємо поточне сортування
          if (sortColumn && sortType) {
            const aValue = getSortValue(a, sortColumn);
            const bValue = getSortValue(b, sortColumn);
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              return sortType === 'asc' 
                ? aValue.localeCompare(bValue) 
                : bValue.localeCompare(aValue);
            }
            
            return sortType === 'asc' 
              ? (aValue > bValue ? 1 : -1)
              : (bValue > aValue ? 1 : -1);
          }
          
          return 0;
        })}
        loading={loading}
        bordered
        cellBordered
        virtualized
        height={tableHeight}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSort}
        onRowClick={(rowData: IApp) => onAppClick(rowData)}
        locale={{ loading: 'Завантаження...' }}
      >
        <Column width={80} align="left" sortable>
          <HeaderCell>ID</HeaderCell>
          <Cell dataKey="id">
            {(rowData: IApp) => (
              <div 
                style={{ display: 'flex', alignItems: 'center', height: '100%' }}
                className="noselect"
              >
                <AppListItem app={rowData} onClick={onAppClick} />
              </div>
            )}
          </Cell>
        </Column>

        <Column width={150} align="left" sortable>
          <HeaderCell>Ім'я</HeaderCell>
          <Cell dataKey="name">
            {(rowData: IApp) => {
              const name = rowData.name || "Не вказано";
              
              return (
                <div className="name-cell noselect">
                  <span>{name}</span>
                  <FontAwesomeIcon 
                    icon={faCopy}
                    className="copy-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(name);
                    }}
                  />
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={1} align="left" sortable>
          <HeaderCell>Bundle</HeaderCell>
          <Cell dataKey="bundle">
            {(rowData: IApp) => {
              const bundle = rowData.bundle || "Не вказано";
              
              return (
                <div className="bundle-cell noselect">
                  {isValidAndroidPackage(bundle) ? (
                    <a 
                      href={getGooglePlayUrl(bundle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bundle-link"
                      style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {bundle}
                    </a>
                  ) : (
                    <span>{bundle}</span>
                  )}
                  <FontAwesomeIcon 
                    icon={faCopy}
                    className="copy-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(bundle);
                    }}
                  />
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column width={280} align="left" sortable>
          <HeaderCell>Статус</HeaderCell>
          <Cell dataKey="status">
            {(rowData: IApp) => {
              const currentStatus = rowData.status;
              
              return (
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusCellClick(rowData.id, e);
                  }}
                  className="status-cell noselect"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: '8px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div 
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(currentStatus, false),
                        flexShrink: 0
                      }}
                      title={currentStatus}
                    />
                    <span style={{ fontSize: '12px' }}>
                      {getStatusText(currentStatus)}
                    </span>
                  </div>
                  <FontAwesomeIcon 
                    icon={faChevronDown}
                    className="status-arrow"
                    style={{ 
                      fontSize: '10px',
                      opacity: 0.7
                    }}
                  />
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Домен</HeaderCell>
          <Cell dataKey="domainParams.name">
            {(rowData: IApp) => {
              const domain = rowData.domainParams?.name || "Не вказано";
              
              return (
                <div 
                  className="domain-cell noselect"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(domain);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <span>{domain}</span>
                  <FontAwesomeIcon 
                    icon={faCopy}
                    className="copy-icon"
                  />
                </div>
              );
            }}
          </Cell>
        </Column>

        {/* Інтеграція column removed - integrationVersion field doesn't exist */}

        {/* Platforms column hidden */}
      </Table>

      {/* Global dropdown positioned outside table */}
      {openDropdownId !== null && dropdownPosition && (() => {
        const app = apps.find(a => a.id === openDropdownId);
        if (!app) return null;
        
        const currentStatus = app.status;
        const availableTransitions = getAvailableStatusTransitions(currentStatus);
        
        return (
          <div
            ref={(el) => {
              if (el) dropdownRefs.current.set(openDropdownId, el);
            }}
            className="status-dropdown"
            style={{
              position: 'absolute',
              left: dropdownPosition.x,
              top: dropdownPosition.y - 12,
              zIndex: 9999,
              backgroundColor: '#2c3e50',
              border: '1px solid #34495e',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              minWidth: '150px'
            }}
          >
            {/* Available transitions */}
            {availableTransitions
              .filter(status => status !== currentStatus)
              .map((status) => (
                <div
                  key={status}
                  onClick={() => onStatusChange(app.id, status)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: currentStatus === status ? '#34495e' : 'transparent',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (currentStatus !== status) {
                      e.currentTarget.style.backgroundColor = '#34495e';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentStatus !== status) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div 
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(status),
                      flexShrink: 0
                    }}
                  />
                  <span style={{ fontSize: '12px', color: '#ecf0f1' }}>
                    {getStatusText(status)}
                  </span>
                </div>
              ))}
          </div>
        );
      })()}
    </div>
  );
};

export default AppsTable; 